import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {Link, withRouter} from 'react-router';
import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import {resource} from '@albert-io/json-api-framework/request/builder';
import {callTargetedAction, useFluxStore} from 'client/framework';
import {drawerActions} from 'client/generic/Drawer/DrawerActions';
import UpgradeSubjectModal from 'components/PracticeView/UpgradeSubjectModal/UpgradeSubjectModal';
import sessionStore from 'client/Session/SessionStore';
import {hasSubjectAccess} from 'lib/UserAccessUtil';
import {SubjectModelV2} from 'resources/GeneratedModels/Subject/SubjectModel.v2';
import './upgrade-subject-button.scss';

/**
 * @todo This should be in a utility somewhere or not required at all (passed via router props)
 */
const domainSlugsRegex = /\/(high-school|middle-school|ela|college|other)\//;

/**
 * Since we have routes that use different named props for the subject
 * slug (`slug`, `subject`), this is a helper method for determining if we are on a single
 * subject based on the provided props object.
 *
 * @param {Object} props
 */
function isSingleSubjectView(props) {
  return props.params.slug || props.params.subject || props.params.subjectSlug || false;
}
/**
 * Retrives the `url_slug` of a subject based on the provided props.
 * @param {Object} props
 */
function getUrlSlugFromProps(props) {
  return props.params.slug || props.params.subject || props.params.subjectSlug;
}

const NO_ACCESS = {isReady: false, value: false};

// `hasSubjectAccess` builds a fresh object per call, so the bridge needs a field-wise
// comparator to keep `getSnapshot` referentially stable across state-tree emits.
const isSameAccess = (a, b) => a.isReady === b.isReady && a.value === b.value;

function UpgradeButtonBase(props) {
  const {subject, params, location} = props;
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const isSingleSubject = isSingleSubjectView(props);
  const isTeacher = useFluxStore(sessionStore, (store) => store.isTeacher());
  const hasValidSession = useFluxStore(sessionStore, (store) => store.hasValidSession());

  const shouldCheckAccess = Boolean(isSingleSubject && hasValidSession);
  const subjectAccess = useFluxStore(
    sessionStore,
    () => (shouldCheckAccess ? hasSubjectAccess(subject.getId()) : NO_ACCESS),
    isSameAccess
  );

  // Parameterised on `isTeacher` so render uses the bridged value while the click
  // handler reads the store at fire time.
  const shouldShowUpgradeCallToAction = (isTeacherNow) =>
    (isTeacherNow && isSingleSubject) || (subject && !subject.getSubscriptionsPrices()?.first());

  const shouldShowCta = shouldShowUpgradeCallToAction(isTeacher);

  const handleClick = () => {
    /**
     * @todo This should possibly be moved to a prop.
     */
    callTargetedAction({
      name: drawerActions.DRAWER_SET_DRAWER_VISIBILITY,
      payload: false,
      targetStore: 'DropdownLinksDrawer'
    });
    if (shouldShowUpgradeCallToAction(sessionStore.isTeacher())) {
      setShowUpgradeModal(true);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      handleClick();
    }
  };

  const getMarketplaceLink = () => {
    /**
     * If we are going to show the "Upgrade" CTA we leave the link
     * empty and expect the modal to contain the actual marketplace link.
     */
    if (shouldShowCta) {
      return '';
    }
    let {domain} = params;
    const {group} = params;
    let subjectUrlString = '';
    if (isSingleSubject) {
      subjectUrlString = `subject=${getUrlSlugFromProps(props)}&subjectId=${subject.getId()}`;
    }

    if (!domain) {
      /**
       * @todo Ideally this can be derieved from router props at some point.
       */
      const result = domainSlugsRegex.exec(location.pathname);
      domain = result ? result[1] : null;
    }

    const queryParams = [];

    if (domain) {
      queryParams.push(`domain=${domain}`);
    }

    if (domain && group) {
      queryParams.push(`group=${group}`);
    }

    if (subjectUrlString) {
      queryParams.push(subjectUrlString);
    }

    const baseUrl = '/marketplace/subjects';
    return queryParams.length === 0 ? baseUrl : `${baseUrl}?${queryParams.join('&')}`;
  };

  if (shouldCheckAccess) {
    const {isReady, value} = subjectAccess;
    if (isReady !== true || value === true) {
      return null;
    }
  }

  const buttonText = isSingleSubject ? 'Upgrade Subject' : 'Upgrade Subjects';

  // If we should show upgrade CTA, render as button to trigger modal
  if (shouldShowCta) {
    return (
      <>
        <button
          type='button'
          className='button button--blue-medium upgrade-subject-button'
          onClick={handleClick}
          onKeyDown={handleKeyDown}
        >
          {buttonText}
        </button>
        {showUpgradeModal && (
          <UpgradeSubjectModal
            subject={subject}
            handleClose={() => setShowUpgradeModal(false)}
          />
        )}
      </>
    );
  }

  // Otherwise render as link for navigation
  return (
    <Link
      className='button button--blue-medium upgrade-subject-button'
      to={getMarketplaceLink()}
      onClick={handleClick}
    >
      {buttonText}
    </Link>
  );
}

UpgradeButtonBase.displayName = 'UpgradeButton';

UpgradeButtonBase.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string
  }),
  params: PropTypes.shape({
    domain: PropTypes.string,
    group: PropTypes.string,
    slug: PropTypes.string
  }),
  subject: PropTypes.instanceOf(SubjectModelV2)
};

export const UpgradeButton = withRouter(
  awaitMandarkQueries((props) => {
    const opts = {
      queries: {}
    };
    if (isSingleSubjectView(props)) {
      opts.queries.subject = resource('subject_v2')
        .mandarkEndpoint(['subjects_v2'])
        .findOne()
        .filter({url_slug: getUrlSlugFromProps(props)});
    }
    return opts;
  }, UpgradeButtonBase)
);
